/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import * as S from "./search-input.styles";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import type { SearchInputProps } from "./types";
import { useCategorizedPackages } from "../../hooks/use-categorized-packages";
import { formatCurrency } from "../../utils/format-currency";
import { useDebouncedValue } from "../../hooks/use-debounce";
import { useTheme } from "styled-components";
import { getResponsiveSrc } from "../../utils/get-responsive-src";

export const SearchInput = ({
    placeholder,
    id,
    value,
    onChange,
    items = [],
    onMatchFound,
}: SearchInputProps) => {
    const { categorizedPackages: packages } = useCategorizedPackages();
    const theme = useTheme().palette;

    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const isNavbarSearch = id === "navbar-search-input";

    const allProducts = useMemo(() => {
        return isNavbarSearch ? (packages?.data ?? []) : items;
    }, [isNavbarSearch, packages?.data, items]);

    const rawQuery = (value ?? "").trim();
    const debouncedQuery = useDebouncedValue(rawQuery.toLowerCase(), 180);

    const filteredProducts = useMemo(() => {
        const q = debouncedQuery;
        if (!q) return [];
        const out: any[] = [];
        for (let i = 0; i < allProducts.length && out.length < 6; i++) {
            const p: any = allProducts[i];
            const label = (p.name ?? p.title ?? "").toLowerCase();
            if (label.includes(q)) out.push(p);
        }
        return out;
    }, [debouncedQuery, allProducts]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isNavbarSearch) return;
        setIsOpen(Boolean(debouncedQuery));
    }, [isNavbarSearch, debouncedQuery]);

    useEffect(() => {
        if (isNavbarSearch) return;
        if (!debouncedQuery) return;
        const match = filteredProducts[0];
        if (match) onMatchFound?.(match.title ?? match.name);
    }, [filteredProducts, onMatchFound, isNavbarSearch, debouncedQuery]);

    return (
        <S.InputWrapper ref={wrapperRef} data-open={isOpen}>
            <S.Search
                id={id}
                placeholder={placeholder}
                type="text"
                autoComplete="off"
                value={value ?? ""}
                onFocus={() => setIsOpen(Boolean(debouncedQuery || filteredProducts.length))}
                onChange={(e) => onChange?.(e.target.value)}
            />
            <S.SearchIcon icon={faSearch} />

            {isNavbarSearch && isOpen && (
                <S.DropdownWrapper>
                    <S.DropdownList>
                        {filteredProducts.length === 0 ? (
                            <S.DropdownItem as="div" style={{ opacity: 0.7, cursor: "default" }}>
                                Nenhum resultado para “{rawQuery}”
                            </S.DropdownItem>
                        ) : (
                            filteredProducts.map((p: any) => {
                                const name = p.name ?? p.title;
                                const slug = p.slug ?? p.id;
                                const price = p.pricing?.price ?? 0;
                                const stock = p.stock?.quantity ?? 0;

                                return (
                                    <S.DropdownItem
                                        as={Link}
                                        to={`/package-info/${slug}`}
                                        key={p.id ?? slug}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        {p.image && (
                                            <img
                                                src={getResponsiveSrc(p.image, 400)}
                                                srcSet={`
                                                    ${getResponsiveSrc(p.image, 200)} 200w,
                                                    ${getResponsiveSrc(p.image, 400)} 400w,
                                                    ${getResponsiveSrc(p.image, 800)} 800w
                                                `}
                                                sizes="(max-width: 768px) 45vw, 200px"
                                                alt={name}
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        )}
                                        <div className="info">
                                            <span className="title">{name}</span>
                                            <div className="bottom">
                                                <span className="price">{formatCurrency(price)}</span>
                                                <span
                                                    className="stock-dot"
                                                    style={{ background: stock > 0 ? theme.success.light : theme.error.medium }}
                                                    aria-hidden
                                                />
                                                <span className="stock-qty">
                                                    {stock > 0 ? `${stock} em estoque` : "Esgotado"}
                                                </span>
                                            </div>
                                        </div>
                                    </S.DropdownItem>
                                );
                            })
                        )}
                    </S.DropdownList>
                </S.DropdownWrapper>
            )}
        </S.InputWrapper>
    );
};
